/* ==========================================================
   Advanced Slider — Web Component
   ========================================================== */

class AdvancedSlider extends HTMLElement {
  connectedCallback() {
    this._slides      = Array.from(this.querySelectorAll('.as-slide'));
    this._dots        = Array.from(this.querySelectorAll('.as-nav-dot'));
    this._total       = this._slides.length;
    this._current     = 0;
    this._duration    = parseInt(this.dataset.duration || 5000, 10);
    this._speed       = parseFloat(this.dataset.speed || 0.7);
    this._autoplay    = this.dataset.autoplay === 'true';
    this._busy        = false;
    this._timer       = null;
    this._isEditor    = !!(window.Shopify && window.Shopify.designMode);

    if (this._total === 0) return;
    this._init();
  }

  disconnectedCallback() {
    this._stopTimer();
  }

  /* ── Init ─────────────────────────────────────────────── */
  _init() {
    // Position all slides instantly (no animation on load)
    this._slides.forEach((slide, i) => {
      slide.classList.add('as-slide--no-anim');
      slide.style.transform = i === 0 ? 'translateX(0)' : 'translateX(100%)';
      slide.style.visibility = i === 0 ? 'visible' : 'hidden';
    });

    this._slides[0].classList.add('is-active');
    this._dots[0]?.classList.add('is-active');

    // Trigger reflow, then remove no-anim flag
    this._slides[0].getBoundingClientRect();
    requestAnimationFrame(() => {
      this._slides.forEach(s => s.classList.remove('as-slide--no-anim'));
    });

    if (this._total > 1) {
      this._bindEvents();
      if (this._autoplay && !this._isEditor) {
        this._startTimer();
      }
    }
  }

  /* ── Navigation ───────────────────────────────────────── */
  _goTo(index, dir) {
    if (this._busy) return;

    const prev = this._current;
    const next = ((index % this._total) + this._total) % this._total;

    if (next === prev) return;

    // Infer direction when not provided
    if (dir === undefined) {
      dir = next > prev ? 1 : -1;
      // Wrap-around edge cases
      if (prev === this._total - 1 && next === 0) dir = 1;
      if (prev === 0 && next === this._total - 1) dir = -1;
    }

    this._busy = true;

    const incoming = this._slides[next];
    const outgoing = this._slides[prev];
    const speed    = this._speed;

    // Snap incoming to its entry position instantly
    incoming.classList.add('as-slide--no-anim');
    incoming.style.visibility  = 'visible';
    incoming.style.transform   = `translateX(${dir * 100}%)`;
    incoming.getBoundingClientRect(); // reflow
    incoming.classList.remove('as-slide--no-anim');

    // Animate both
    const easing = `transform ${speed}s cubic-bezier(0.4, 0, 0.2, 1)`;
    outgoing.style.transition = easing;
    incoming.style.transition = easing;

    outgoing.style.transform = `translateX(${-dir * 100}%)`;
    incoming.style.transform = 'translateX(0)';

    // Swap active classes
    outgoing.classList.remove('is-active');
    incoming.classList.add('is-active');
    this._current = next;

    // Update dot + progress bar
    this._dots[prev]?.classList.remove('is-active');
    const dot = this._dots[next];
    if (dot) {
      // Force CSS transition restart by removing/re-adding class
      dot.classList.remove('is-active');
      dot.getBoundingClientRect();
      dot.classList.add('is-active');
    }

    // Cleanup after animation
    setTimeout(() => {
      outgoing.style.transition  = '';
      outgoing.style.visibility  = 'hidden';
      incoming.style.transition  = '';
      this._busy = false;
    }, speed * 1000 + 50);
  }

  _next() {
    this._goTo((this._current + 1) % this._total, 1);
  }

  _prev() {
    const idx = (this._current - 1 + this._total) % this._total;
    this._goTo(idx, -1);
  }

  /* ── Timer ────────────────────────────────────────────── */
  _startTimer() {
    this._stopTimer();
    this._timer = setInterval(() => this._next(), this._duration);
  }

  _stopTimer() {
    clearInterval(this._timer);
    this._timer = null;
  }

  _resetTimer() {
    if (this._autoplay && !this._isEditor) this._startTimer();
  }

  /* ── Events ───────────────────────────────────────────── */
  _bindEvents() {
    // Dot clicks
    this._dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const dir = i > this._current ? 1 : -1;
        this._goTo(i, dir);
        this._resetTimer();
      });
    });

    // Touch / swipe
    let startX = 0;
    let startY = 0;
    this.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    this.addEventListener('touchend', e => {
      const dx = startX - e.changedTouches[0].clientX;
      const dy = startY - e.changedTouches[0].clientY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx > 0 ? this._next() : this._prev();
        this._resetTimer();
      }
    }, { passive: true });

    // Pause autoplay on hover (desktop only)
    this.addEventListener('mouseenter', () => this._stopTimer());
    this.addEventListener('mouseleave', () => {
      if (this._autoplay && !this._isEditor) this._startTimer();
    });

    // Keyboard
    this.addEventListener('keydown', e => {
      if (e.key === 'ArrowRight') { this._next(); this._resetTimer(); }
      if (e.key === 'ArrowLeft')  { this._prev(); this._resetTimer(); }
    });

    // Theme editor — jump to selected block
    document.addEventListener('shopify:block:select', e => {
      const slide = e.target?.closest('.as-slide');
      if (!slide) return;
      const idx = this._slides.indexOf(slide);
      if (idx > -1) {
        this._stopTimer();
        this._goTo(idx);
      }
    });

    document.addEventListener('shopify:block:deselect', () => {
      if (this._autoplay && !this._isEditor) this._startTimer();
    });
  }
}

if (!customElements.get('advanced-slider')) {
  customElements.define('advanced-slider', AdvancedSlider);
}
